import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  Copy,
  Radio,
  Circle,
} from 'lucide-react';
import { useGitStatus } from '@/hooks/useGitStatus';
import { useToast } from '@/hooks/useToast';
import { useGit } from '@/hooks/useGit';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostr } from '@/hooks/useNostr';
import { AnnounceRepositoryDialog } from './AnnounceRepositoryDialog';

interface NostrPublishingViewProps {
  projectId: string;
  remoteUrl?: string | null;
}

export function NostrPublishingView({ projectId, remoteUrl }: NostrPublishingViewProps) {
  const [isPublished, setIsPublished] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [cloneUrl, setCloneUrl] = useState('');

  const { data: gitStatus } = useGitStatus(projectId);
  const { toast } = useToast();
  const { git } = useGit();
  const { user } = useCurrentUser();
  const { nostr } = useNostr();

  const projectPath = `/projects/${projectId}`;
  const commitsToUpdate = gitStatus?.ahead || 0;

  useEffect(() => {
    // Check if repository is published to Nostr
    if (remoteUrl?.startsWith('nostr://')) {
      setIsPublished(true);
      setCloneUrl(remoteUrl);
    }
  }, [remoteUrl]);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(cloneUrl);
    toast({
      title: 'Copied to clipboard',
      description: 'Clone URL copied successfully',
    });
  };

  const handleUpdateNostr = async () => {
    if (!user || !user.signer) {
      toast({
        title: 'Authentication required',
        description: 'Please log in with Nostr to update',
        variant: 'destructive',
      });
      return;
    }

    setIsUpdating(true);
    try {
      // Push changes to Nostr git
      await git.push({
        dir: projectPath,
        ref: gitStatus?.currentBranch || 'main',
      });

      toast({
        title: 'Repository updated!',
        description: 'Your changes have been pushed to Nostr',
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      toast({
        title: 'Failed to update repository',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      {isPublished ? (
        <div className="space-y-4">
          {/* Published status */}
          <Card className="border-green-600/20 bg-green-950/20">
            <CardContent className="py-4 px-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-4.5 w-4.5 text-green-500 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold mb-0.5">Published to Nostr</h4>
                  <p className="text-xs text-muted-foreground">
                    Your repository is available on the decentralized Nostr network
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Clone URL */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Clone URL</Label>
            <div className="flex gap-2">
              <Input
                value={cloneUrl}
                readOnly
                className="flex-1 font-mono text-xs h-9 bg-muted/50"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={handleCopyUrl}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Update status and button */}
          {commitsToUpdate > 0 && (
            <div className="space-y-3">
              {/* Unsaved changes card */}
              <Card className="bg-purple-950/40 border-purple-500/20">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2.5">
                    <Circle className="h-2 w-2 fill-purple-500 text-purple-500 shrink-0" />
                    <span className="text-white text-sm font-medium">
                      {commitsToUpdate} unsaved change{commitsToUpdate !== 1 ? 's' : ''}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Save to Nostr button */}
              <Button
                onClick={handleUpdateNostr}
                disabled={isUpdating}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white h-10"
              >
                <Radio className="h-4 w-4 mr-2" />
                Save to Nostr
              </Button>
            </div>
          )}
        </div>
      ) : (
        <AnnounceRepositoryDialog
          projectId={projectId}
          remoteUrl={remoteUrl || undefined}
        />
      )}
    </div>
  );
}
